import React from "react"

import { useHistory } from "react-router-dom"
import { gql, useMutation, useQuery } from "urql"
import * as Yup from "yup"

import { SelectOption } from "~tailwindui/types/SelectOption"

import WithLoadingIndicator from "~tailwindui/Basics/WithLoadingIndicator"

import { timezoneOptions } from "src/enums"
import ordered from "src/ordered"

import DisplayableFormikForm from "components/OrganizationAdmin/components/DisplayableFormikForm"

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required."),
  lastName: Yup.string().required("Last Name is required."),
  email: Yup.string().required("Email is required."),
  emailTimeZone: Yup.string().required("Please select a time zone."),
  schoolIds: Yup.array().when("canOrganizationAdminAccessAllSchools", {
    is: (canAllSchools: boolean) => !canAllSchools,
    then: Yup.array().min(
      1,
      "Please choose the school(s) which this user can access."
    ),
  }),
})

const UserNew = () => {
  const history = useHistory()

  const buildFormState = viewer => ({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    emailTimeZone: viewer.organization.emailTimeZone,
    canOrganizationAdminAccessAllSchools: false,
    canOrganizationAdminAccessFinances: false,
    canOrganizationAdminAccessUserManagement: false,
    canOrganizationAdminAccessSemesters: false,
    schoolIds: [],
  })
  const [viewerResult] = useQuery({ query: viewerQuery })

  const [, createOrganizationAdmin] = useMutation(
    createOrganizationAdminMutation
  )

  return (
    <WithLoadingIndicator result={viewerResult}>
      {({ data: { viewer } }) => (
        <DisplayableFormikForm.Wrapper
          title="Add New Admin"
          editable={true}
          editing={true}
          displayData={buildFormState(viewer)}
          initialValues={buildFormState(viewer)}
          validationSchema={validationSchema}
          mutation={{
            name: "createOrganizationAdmin",
            run: createOrganizationAdmin,
          }}
          onCancel={() => history.push("/users")}
          convertFormValues={vals => ({ input: vals })}
          onSuccess={() => history.push("/users")}
        >
          {({ values }) => (
            <>
              <DisplayableFormikForm.EditOnly>
                <DisplayableFormikForm.Section title="User Account">
                  <DisplayableFormikForm.TextInput
                    label="First Name"
                    name="firstName"
                  />
                  <DisplayableFormikForm.TextInput
                    label="Last Name"
                    name="lastName"
                  />
                  <DisplayableFormikForm.TextInput label="Email" name="email" />
                  <DisplayableFormikForm.TextInput
                    label="Username"
                    name="username"
                  />
                  <DisplayableFormikForm.SingleSelect
                    label="Time Zone"
                    name="emailTimeZone"
                    options={
                      ordered(timezoneOptions, "label") as SelectOption[]
                    }
                    description="This is used to determine notification times."
                  />
                </DisplayableFormikForm.Section>
                <DisplayableFormikForm.Section title="Permissions">
                  <DisplayableFormikForm.ToggleInput
                    label="Financial Access"
                    name="canOrganizationAdminAccessFinances"
                    description="Allows this user to view financial data."
                  />
                  <DisplayableFormikForm.ToggleInput
                    label="Semesters Access"
                    name="canOrganizationAdminAccessSemesters"
                    description="Allows this user to create and edit your organization's academic semesters."
                  />
                  <DisplayableFormikForm.ToggleInput
                    label="User Management Access"
                    name="canOrganizationAdminAccessUserManagement"
                    description="Allows this user to create and edit other users."
                  />
                  <DisplayableFormikForm.ToggleInput
                    label="All Schools Access"
                    name="canOrganizationAdminAccessAllSchools"
                    description="Allows this user to view data for all schools."
                  />
                  {!values.canOrganizationAdminAccessAllSchools && (
                    <DisplayableFormikForm.MultiSelect
                      label="Permitted School(s)"
                      name="schoolIds"
                      description="The schools this user has access to."
                      options={ordered(viewer.organization.schools, "name").map(
                        ({ id, name }) => ({
                          label: name,
                          value: id,
                        })
                      )}
                    />
                  )}
                </DisplayableFormikForm.Section>
              </DisplayableFormikForm.EditOnly>
            </>
          )}
        </DisplayableFormikForm.Wrapper>
      )}
    </WithLoadingIndicator>
  )
}

const viewerQuery = gql`
  query viewer {
    viewer {
      organization {
        schools {
          id
          name
        }
        emailTimeZone
      }
    }
  }
`

const createOrganizationAdminMutation = gql`
  mutation ($input: OrganizationAdminInputObject!) {
    createOrganizationAdmin(input: $input) {
      redirectTo
      organizationAdmin {
        id
      }
      failures {
        message
      }
    }
  }
`

export default UserNew
